import { useMemo, useState } from "react";
import { MapPin, Search } from "lucide-react";
import { formatRupiah } from "@/lib/billing";

const TH = "px-2 sm:px-3 py-2 font-semibold text-[10px] uppercase whitespace-nowrap bg-[#F3F4F6] sticky top-0 z-10";
const TD_NUM = "px-2 sm:px-3 py-2 text-right font-mono text-xs sm:text-sm whitespace-nowrap";

const pct = (lunas, total) => (total > 0 ? (lunas * 100) / total : 0);

function PctBar({ value, testid }) {
  const color = value >= 70 ? "bg-[#10B981]" : value >= 40 ? "bg-[#F59E0B]" : "bg-[#EF4444]";
  return (
    <div className="flex items-center gap-2 justify-end" data-testid={testid}>
      <span className="font-mono text-xs sm:text-sm w-12 text-right">{value.toFixed(1)}%</span>
      <div className="hidden sm:block w-20 h-2 rounded-full bg-[#E5E7EB] overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${Math.min(100, value)}%` }} />
      </div>
    </div>
  );
}

function WilayahRow({ r, idx }) {
  const none = r.wilayah_id == null;
  const tid = none ? "pw-row-tanpa-wilayah" : `pw-row-${r.wilayah_id}`;
  return (
    <tr className={none ? "bg-[#FAFAFA]" : idx % 2 === 1 ? "bg-[#F9FAFB]" : ""} data-testid={tid}>
      <td className="px-2 sm:px-3 py-2 text-xs text-[#6B7280]">{none ? "—" : idx + 1}</td>
      <td className={`px-2 sm:px-3 py-2 text-xs sm:text-sm ${none ? "italic text-[#6B7280]" : "font-semibold text-[#111827]"}`}>
        {r.nama_wilayah}
        {!none && <div className="text-[9px] text-[#9CA3AF] font-mono">{r.wilayah_id}</div>}
      </td>
      <td className={`${TD_NUM} text-[#1E3A8A]`}>{formatRupiah(r.total_tagihan)}</td>
      <td className={`${TD_NUM} text-[#065F46]`}>{formatRupiah(r.total_dibayar)}</td>
      <td className={`${TD_NUM} text-[#92400E]`}>{formatRupiah(r.total_penerimaan_kas)}</td>
      <td className={`${TD_NUM} text-[#991B1B]`}>{formatRupiah(r.total_sisa)}</td>
      <td className={`${TD_NUM} text-[#374151]`}>{(r.count_customer || 0).toLocaleString("id-ID")}</td>
      <td className="px-2 sm:px-3 py-2"><PctBar value={pct(r.count_lunas, r.count_invoice)} testid={`${tid}-pct`} /></td>
    </tr>
  );
}

export const PerWilayahTable = ({ rows = [], loading }) => {
  const [q, setQ] = useState("");
  const sum = (k) => rows.reduce((n, r) => n + (r[k] || 0), 0);
  const total = useMemo(() => ({
    tagihan: sum("total_tagihan"), dibayar: sum("total_dibayar"), kas: sum("total_penerimaan_kas"),
    sisa: sum("total_sisa"), cust: sum("count_customer"), inv: sum("count_invoice"), lunas: sum("count_lunas"),
  }), [rows]); // eslint-disable-line react-hooks/exhaustive-deps
  const named = rows.filter((r) => r.wilayah_id != null);
  const none = rows.find((r) => r.wilayah_id == null);
  const term = q.trim().toLowerCase();
  const shown = term
    ? named.filter((r) => `${r.nama_wilayah} ${r.wilayah_id}`.toLowerCase().includes(term))
    : named;

  return (
    <div className="rounded-lg border bg-white p-3 sm:p-4" data-testid="dashboard-per-wilayah">
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <h3 className="font-black text-sm sm:text-base text-[#111827] flex items-center gap-2">
          <MapPin className="w-4 h-4" /> Ringkasan per Wilayah
          <span className="text-[10px] font-semibold text-[#6B7280]" data-testid="pw-count">
            ({named.length.toLocaleString("id-ID")} wilayah)
          </span>
        </h3>
        <div className="relative w-full sm:w-64">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama wilayah…"
                 className="w-full rounded-md border border-[#E5E7EB] pl-7 pr-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30"
                 data-testid="pw-search-input" />
        </div>
      </div>
      {loading ? (
        <div className="text-xs text-[#6B7280] py-10 text-center">Memuat…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-[#6B7280] py-10 text-center" data-testid="pw-empty">
          Belum ada data wilayah pada rentang ini.
        </div>
      ) : (
        <div className="overflow-auto max-h-[480px] -mx-3 sm:-mx-4 border-y" data-testid="pw-scroll">
          <table className="min-w-full text-xs sm:text-sm" data-testid="pw-table">
            <thead className="text-[#374151]">
              <tr>
                <th className={`${TH} text-left`}>#</th>
                <th className={`${TH} text-left`}>Wilayah</th>
                <th className={`${TH} text-right`}>Total Tagihan</th>
                <th className={`${TH} text-right`}>Pembayaran atas Tagihan</th>
                <th className={`${TH} text-right`}>Penerimaan Kas</th>
                <th className={`${TH} text-right`}>Sisa Tagihan</th>
                <th className={`${TH} text-right`}>Pelanggan</th>
                <th className={`${TH} text-right`}>% Lunas</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E7EB]">
              {shown.map((r, idx) => <WilayahRow key={r.wilayah_id} r={r} idx={idx} />)}
              {term && shown.length === 0 && (
                <tr><td colSpan={8} className="py-6 text-center text-xs text-[#6B7280]" data-testid="pw-no-match">
                  Tidak ada wilayah yang cocok.
                </td></tr>
              )}
              {none && !term && <WilayahRow r={none} idx={-1} />}
            </tbody>
            <tfoot className="sticky bottom-0 z-10">
              <tr className="bg-[#EFF6FF] font-black text-[#1E3A8A]" data-testid="pw-row-total">
                <td className="px-2 sm:px-3 py-2" />
                <td className="px-2 sm:px-3 py-2 text-xs sm:text-sm">TOTAL{term ? " (semua wilayah)" : ""}</td>
                <td className={TD_NUM} data-testid="pw-total-tagihan">{formatRupiah(total.tagihan)}</td>
                <td className={TD_NUM} data-testid="pw-total-dibayar">{formatRupiah(total.dibayar)}</td>
                <td className={TD_NUM} data-testid="pw-total-kas">{formatRupiah(total.kas)}</td>
                <td className={TD_NUM} data-testid="pw-total-sisa">{formatRupiah(total.sisa)}</td>
                <td className={TD_NUM} data-testid="pw-total-pelanggan">{total.cust.toLocaleString("id-ID")}</td>
                <td className="px-2 sm:px-3 py-2"><PctBar value={pct(total.lunas, total.inv)} testid="pw-total-pct" /></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      <div className="text-[10px] text-[#6B7280] mt-2 leading-snug">
        Berdasarkan wilayah pelanggan saat ini. Tagihan/Dibayar/Sisa/% Lunas berbasis invoice aktif (DIBATALKAN &amp; sumber carry-forward dikecualikan). Penerimaan Kas = payment CONFIRMED by tanggal bayar. % Lunas TOTAL = Σ invoice LUNAS ÷ Σ invoice (bukan rata-rata persentase). Total ≡ KPI Dashboard.
      </div>
    </div>
  );
};
